import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CountDownComponent.scss";

const parseWeddingDate = (rawDate) => {
  if (!rawDate) return null;

  if (rawDate instanceof Date && !Number.isNaN(rawDate.getTime())) {
    return rawDate;
  }

  const value = String(rawDate).trim();
  if (!value) return null;

  const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch;
    const parsed = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);

    if (
      parsed.getFullYear() === Number(y) &&
      parsed.getMonth() === Number(m) - 1 &&
      parsed.getDate() === Number(d)
    ) {
      return parsed;
    }
    return null;
  }

  const dmyMatch = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmyMatch) {
    const [, d, m, y] = dmyMatch;
    const parsed = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);

    if (
      parsed.getFullYear() === Number(y) &&
      parsed.getMonth() === Number(m) - 1 &&
      parsed.getDate() === Number(d)
    ) {
      return parsed;
    }
    return null;
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const CountDownComponent = ({
  title = "Counting down to our big day",
  weddingData,
  loading = false,
}) => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const ceremonyTypeRaw = weddingData?.ceremony_type || "wedding";

  const isHomeComing = useMemo(() => {
    const normalized = String(ceremonyTypeRaw).trim().toLowerCase();
    return (
      normalized === "home coming" ||
      normalized === "homecoming" ||
      normalized === "home-coming"
    );
  }, [ceremonyTypeRaw]);

  const dynamicTitle = isHomeComing
    ? "Counting down to our homecoming"
    : title;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  const target = useMemo(() => {
    return parseWeddingDate(weddingData?.wedding_date);
  }, [weddingData?.wedding_date]);

  const calc = () => {
    if (!target) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        pctDays: 0,
        pctSeconds: 0,
        pctMinutes: 0,
        pctHours: 0,
      };
    }

    const now = new Date();
    let diff = Math.max(0, target.getTime() - now.getTime());

    const days = Math.floor(diff / 86400000);
    diff -= days * 86400000;

    const hours = Math.floor(diff / 3600000);
    diff -= hours * 3600000;

    const minutes = Math.floor(diff / 60000);
    diff -= minutes * 60000;

    const seconds = Math.floor(diff / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      pctDays: Math.min(days / 365, 1),
      pctSeconds: seconds / 60,
      pctMinutes: minutes / 60,
      pctHours: hours / 24,
    };
  };

  const [time, setTime] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    pctDays: 0,
    pctSeconds: 0,
    pctMinutes: 0,
    pctHours: 0,
  });

  const [flip, setFlip] = useState({ d: false, h: false, m: false, s: false });
  const prev = useRef({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    pctDays: 0,
    pctSeconds: 0,
    pctMinutes: 0,
    pctHours: 0,
  });

  useEffect(() => {
    const next = calc();
    setTime(next);
    prev.current = next;

    if (!target) return;

    const iv = setInterval(() => {
      const updated = calc();

      setFlip({
        d: prev.current.days !== updated.days,
        h: prev.current.hours !== updated.hours,
        m: prev.current.minutes !== updated.minutes,
        s: prev.current.seconds !== updated.seconds,
      });

      setTime(updated);
      prev.current = updated;

      setTimeout(() => {
        setFlip({ d: false, h: false, m: false, s: false });
      }, 180);
    }, 1000);

    return () => clearInterval(iv);
  }, [target]);

  const pad2 = (n) => String(n).padStart(2, "0");

  const done =
    !!target &&
    time.days === 0 &&
    time.hours === 0 &&
    time.minutes === 0 &&
    time.seconds === 0;

  const showMissingDate = !loading && weddingData && !target;

  return (
    <section
      ref={sectionRef}
      className={`cdNeo ${visible ? "is-visible" : ""}`}
      aria-label={isHomeComing ? "Homecoming countdown" : "Wedding countdown"}
    >
      <div className="cdNeo__vignette" aria-hidden="true" />

      <div className="cdNeo__inner">
        <header className="cdNeo__head">
          <span className="cdNeo__script">{dynamicTitle}</span>
        </header>

        <div className="cdNeo__row">
          <div
            className={`ring ${flip.d ? "is-flip" : ""}`}
            aria-label={`${time.days} days`}
            style={{ "--pct": time.pctDays }}
          >
            <div className="ring__meter ring__meter--days" />
            <div className="ring__core">
              <div className="ring__value">{time.days}</div>
              <div className="ring__label">Days</div>
            </div>
          </div>

          <div
            className={`ring ${flip.h ? "is-flip" : ""}`}
            aria-label={`${pad2(time.hours)} hours`}
            style={{ "--pct": time.pctHours }}
          >
            <div className="ring__meter" />
            <div className="ring__core">
              <div className="ring__value">{pad2(time.hours)}</div>
              <div className="ring__label">Hours</div>
            </div>
          </div>

          <div
            className={`ring ${flip.m ? "is-flip" : ""}`}
            aria-label={`${pad2(time.minutes)} minutes`}
            style={{ "--pct": time.pctMinutes }}
          >
            <div className="ring__meter" />
            <div className="ring__core">
              <div className="ring__value">{pad2(time.minutes)}</div>
              <div className="ring__label">Minutes</div>
            </div>
          </div>

          <div
            className={`ring ${flip.s ? "is-flip" : ""}`}
            aria-label={`${pad2(time.seconds)} seconds`}
            style={{ "--pct": time.pctSeconds }}
          >
            <div className="ring__meter" />
            <div className="ring__core">
              <div className="ring__value">{pad2(time.seconds)}</div>
              <div className="ring__label">Seconds</div>
            </div>
          </div>
        </div>

        {loading && <p className="cdNeo__msg">Loading countdown...</p>}

        {showMissingDate && (
          <p className="cdNeo__msg">
            {isHomeComing
              ? "Homecoming date not set yet."
              : "Wedding date not set yet."}
          </p>
        )}

        {done && (
          <p className="cdNeo__msg">
            {isHomeComing ? "It’s homecoming time! 💫" : "It’s wedding time! 💫"}
          </p>
        )}
      </div>
    </section>
  );
};

export default CountDownComponent;